import { t } from "@/lib/i18n"

import { CapabilityRow, LandingSection, SectionRule, SectionTitle } from "./section-rule"

type CapabilityItem = { key: string; shipped: boolean }
type CapabilityGroup = { key: string; items: CapabilityItem[] }

/**
 * `shipped` mirrors `default_enabled` in `config/location_features.yml`: true
 * means the capability works the moment a location is created, false means the
 * restaurant switches it on. Only keys with `default_available: true` appear
 * here at all — that file is the MVP scope, and a spec pins it both ways, so
 * this list cannot promise something admin has not granted.
 *
 * `core` covers behaviour that is not a feature key: the shared bill, hybrid
 * ordering, the split, payment methods, tips, the event log, and roles.
 */
const columns: CapabilityGroup[][] = [
  [
    {
      key: "core",
      items: [
        { key: "shared_bill", shipped: true },
        { key: "hybrid_orders", shipped: true },
        { key: "item_split", shipped: true },
        { key: "payment_methods", shipped: true },
        { key: "tips", shipped: true },
        { key: "audit_log", shipped: true },
        { key: "roles", shipped: true },
      ],
    },
    {
      key: "finance",
      items: [
        { key: "reports", shipped: true },
        { key: "taxes", shipped: false },
      ],
    },
    { key: "expansion", items: [{ key: "multi_location", shipped: true }] },
  ],
  [
    {
      key: "service",
      items: [
        { key: "waiter_call", shipped: true },
        { key: "pickup", shipped: false },
        { key: "public_menu", shipped: false },
      ],
    },
    {
      key: "operation",
      items: [
        { key: "kds", shipped: false },
        { key: "cash_register", shipped: false },
        { key: "discounts", shipped: false },
        { key: "refunds", shipped: false },
        { key: "dynamic_invites", shipped: false },
        { key: "item_stock", shipped: false },
      ],
    },
  ],
]

export function CapabilitiesSection() {
  return (
    <LandingSection id="product">
      <SectionRule>{t("ui.landing.capabilities.label")}</SectionRule>

      <div className="mt-6 flex flex-col items-start justify-between gap-6 border-b border-brand pb-6 md:flex-row md:items-end md:gap-12">
        <SectionTitle className="max-w-[16em]">{t("ui.landing.capabilities.title")}</SectionTitle>
        <div className="flex flex-col gap-2 pb-1.5">
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="size-2 rounded-full bg-brand" aria-hidden />
            {t("ui.landing.capabilities.legend_shipped")}
          </span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="size-2 rounded-full border border-brand/50" aria-hidden />
            {t("ui.landing.capabilities.legend_optional")}
          </span>
        </div>
      </div>

      <div className="mt-2 grid gap-x-16 md:grid-cols-2">
        {columns.map((groups, columnIndex) => (
          <div key={columnIndex} className="flex flex-col">
            {groups.map((group) => (
              <div key={group.key} className="flex flex-col">
                <p className="pb-2.5 pt-6 text-xs font-semibold text-muted-foreground">
                  {t(`ui.landing.capabilities.groups.${group.key}`)}
                </p>
                {group.items.map((item, index) => (
                  <CapabilityRow
                    key={item.key}
                    title={t(`ui.landing.capabilities.items.${item.key}.title`)}
                    description={t(`ui.landing.capabilities.items.${item.key}.description`)}
                    shipped={item.shipped}
                    last={index === group.items.length - 1}
                  />
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </LandingSection>
  )
}
